import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useParams } from "react-router";
import { ArrowUpRight, Loader2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { VirtualizedCombobox } from "@/components/ui/virtualized-combobox";
import { cn, GICSSubIndustries } from "@/lib/utils";
import {
  ECOSYSTEM_FUNDING_MODELS,
  ECOSYSTEM_OWNERSHIP_TYPES,
  ECOSYSTEM_PLAYER_TYPES,
  ECOSYSTEM_REGIONS,
  ECOSYSTEMS,
  MAX_GICS_SUB_INDUSTRIES,
  ecosystemMapUrl,
} from "@/lib/ecosystem-taxonomy";
import {
  ecosystemApi,
  EcosystemSubmissionError,
  type EcosystemSubmissionPayload,
  type EcosystemSubmissionResult,
} from "@/api/ecosystem-api";
import {
  EcoAsideCard,
  EcoBadge,
  EcoButton,
  EcoContainer,
  EcoCrumbs,
  EcoEyebrow,
  EcoFooter,
  EcoHeader,
  EcoSectionLabel,
  EcoShell,
} from "./eco-ui";

/**
 * /ecosystem/:ecosystem/submit — intake for the ecosystem maps.
 *
 * An organization describes itself in the map's own vocabulary and the record
 * lands in a curation queue; nothing typed here is published directly. The page
 * is laid out as the profile it would become — the same two-column grid, the
 * same definition-list of facts — so a submitter can see the shape of the entry
 * they're filing, and the confirmation can show it back to them.
 *
 * Public and unauthenticated by design: the people best placed to correct a map
 * entry are the organizations on it.
 */

/** Sentinel for "not stated" — Radix Select forbids an empty item value. */
const NONE = "__none";

interface FormState {
  name: string;
  website: string;
  description: string;
  player_type: string;
  sector_focus: string;
  funding_model: string;
  funding_detail: string;
  ownership: string;
  city: string;
  region: string;
  country: string;
  street: string;
  line2: string;
  postal_code: string;
  linkedin: string;
  source_url: string;
  submitter_name: string;
  submitter_email: string;
  submitter_role: string;
}

const emptyForm = (defaultCountry: string): FormState => ({
  name: "",
  website: "",
  description: "",
  player_type: "",
  sector_focus: "",
  funding_model: "",
  funding_detail: "",
  ownership: "",
  city: "",
  region: "",
  country: defaultCountry,
  street: "",
  line2: "",
  postal_code: "",
  linkedin: "",
  source_url: "",
  submitter_name: "",
  submitter_email: "",
  submitter_role: "",
});

const DESCRIPTION_LIMIT = 600;
const MAX_TAGS = 8;

/** A labelled field with its inline error. `hint` explains the map's usage. */
function Field({
  id,
  label,
  required,
  hint,
  error,
  children,
}: {
  id: string;
  label: string;
  required?: boolean;
  hint?: ReactNode;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label
        htmlFor={id}
        className="text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground"
      >
        {label}
        {required && (
          <span className="ml-1 text-destructive" aria-hidden="true">
            *
          </span>
        )}
      </Label>
      {children}
      {hint && !error && (
        <p className="text-[11px] leading-relaxed text-muted-foreground">{hint}</p>
      )}
      {error && (
        <p role="alert" className="text-[11px] font-medium text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

/** A group of fields under a small-caps heading, ruled off like `.eco-facts`. */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border-t border-border pt-6">
      <EcoSectionLabel>{title}</EcoSectionLabel>
      <div className="mt-5 grid gap-5 sm:grid-cols-2">{children}</div>
    </section>
  );
}

/** Full-width cell inside a Section's two-column grid. */
function Wide({ children }: { children: ReactNode }) {
  return <div className="sm:col-span-2">{children}</div>;
}

export default function EcosystemIntake() {
  const { ecosystem: ecosystemParam } = useParams<{ ecosystem: string }>();
  const ecosystemKey = (ecosystemParam ?? "canada").toLowerCase();
  const ecosystem = ECOSYSTEMS[ecosystemKey];

  const [form, setForm] = useState<FormState>(() =>
    emptyForm(ecosystem?.defaultCountry ?? "Canada")
  );
  const [gicsCodes, setGicsCodes] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [tagDraft, setTagDraft] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<
    (EcosystemSubmissionResult & { name: string }) | null
  >(null);

  useEffect(() => {
    document.title = ecosystem
      ? `Add an organization | ${ecosystem.label} Ecosystem | StartupFuel`
      : "Ecosystem | StartupFuel";
  }, [ecosystem]);

  const set = (key: keyof FormState) => (value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    // Clearing on edit keeps a server error from outliving the thing it was
    // about; the form re-validates on the next submit anyway.
    setFieldErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  /**
   * The GICS table is 1,400 entries, so it is built once and handed to a
   * virtualized combobox. Descriptions ride along — the combobox shows each as
   * an info tooltip, which is the difference between picking a sub-industry and
   * guessing at one.
   */
  const gicsOptions = useMemo(
    () =>
      GICSSubIndustries.map((si) => ({
        value: si.code,
        label: si.name,
        description: si.description,
      })),
    []
  );

  /**
   * The combobox reports one code at a time. The cap is enforced on the way in
   * rather than on submit — an over-limit selection the backend would reject is
   * better refused at the click than surfaced as an error four fields later.
   */
  const toggleGics = (code: string) => {
    setFieldErrors((prev) => {
      if (!("gics_sub_industry" in prev)) return prev;
      const next = { ...prev };
      delete next.gics_sub_industry;
      return next;
    });
    setGicsCodes((prev) => {
      if (prev.includes(code)) return prev.filter((c) => c !== code);
      if (prev.length >= MAX_GICS_SUB_INDUSTRIES) return prev;
      return [...prev, code];
    });
  };

  const addTag = (raw: string) => {
    const cleaned = raw.trim().replace(/,$/, "").slice(0, 40);
    if (!cleaned) return;
    setTags((prev) =>
      prev.includes(cleaned) || prev.length >= MAX_TAGS ? prev : [...prev, cleaned]
    );
    setTagDraft("");
  };

  const payload = useMemo<EcosystemSubmissionPayload>(() => {
    const optional = (value: string) => (value.trim() ? value.trim() : undefined);
    return {
      ecosystem: ecosystemKey,
      name: form.name.trim(),
      player_type: form.player_type,
      description: form.description.trim(),
      website: form.website.trim(),
      city: form.city.trim(),
      region: form.region,
      country: optional(form.country),
      funding_model: optional(form.funding_model),
      funding_detail: optional(form.funding_detail),
      ownership: optional(form.ownership),
      gics_sub_industry: gicsCodes,
      sector_focus: optional(form.sector_focus),
      tags,
      linkedin: optional(form.linkedin),
      source_url: optional(form.source_url),
      // Sent only when a street was given — without one it would just repeat
      // the city and province already on the record, which is what the API
      // does with it anyway.
      hq_address: form.street.trim()
        ? {
            street: form.street.trim(),
            line2: optional(form.line2),
            city: optional(form.city),
            region: form.region,
            postal_code: optional(form.postal_code),
            country: optional(form.country),
          }
        : undefined,
      submitter_name: form.submitter_name.trim(),
      submitter_email: form.submitter_email.trim(),
      submitter_role: optional(form.submitter_role),
    };
  }, [ecosystemKey, form, gicsCodes, tags]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    setFormError(null);
    setFieldErrors({});

    try {
      const result = await ecosystemApi.submit(payload);
      setSubmitted({ ...result, name: payload.name });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      if (error instanceof EcosystemSubmissionError) {
        if (error.fieldErrors.length > 0) {
          setFieldErrors(
            Object.fromEntries(error.fieldErrors.map((e) => [e.field, e.message]))
          );
        }
        setFormError(error.message);
      } else {
        setFormError("Could not save your submission. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const startAnother = () => {
    setForm(emptyForm(ecosystem?.defaultCountry ?? "Canada"));
    setGicsCodes([]);
    setTags([]);
    setTagDraft("");
    setFieldErrors({});
    setFormError(null);
    setSubmitted(null);
  };

  if (!ecosystem) {
    return (
      <EcoShell>
        <EcoHeader ecosystem="canada" />
        <EcoContainer className="flex flex-1 flex-col items-center justify-center py-24 text-center">
          <EcoEyebrow>NOT FOUND</EcoEyebrow>
          <h1 className="eco-display mt-4 text-[clamp(2rem,4vw,3.2rem)]">
            No such ecosystem
          </h1>
          <p className="mt-5 max-w-md text-[15px] leading-relaxed text-muted-foreground">
            We only take submissions for maps that exist. Canada is the one we
            publish today.
          </p>
          <EcoButton as="a" href="/ecosystem/canada/submit" className="mt-7">
            Submit to the Canada map
          </EcoButton>
        </EcoContainer>
        <EcoFooter />
      </EcoShell>
    );
  }

  const mapUrl = ecosystemMapUrl(ecosystem.key);

  return (
    <EcoShell>
      <EcoHeader ecosystem={ecosystem.key} />

      <main className="flex-1 bg-card py-12 sm:py-16">
        <EcoContainer>
          <EcoCrumbs
            items={[
              { label: "Ecosystem", href: mapUrl },
              { label: ecosystem.label, href: mapUrl },
              { label: submitted ? "Submitted" : "Add an organization" },
            ]}
          />

          {submitted ? (
            <SubmittedPanel
              name={submitted.name}
              slug={submitted.slug}
              ecosystemLabel={ecosystem.label}
              mapUrl={mapUrl}
              onAnother={startAnother}
            />
          ) : (
            <div className="mt-7 grid gap-10 lg:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)] lg:gap-14">
              <div>
                <EcoEyebrow>{ecosystem.label.toUpperCase()} ECOSYSTEM INTAKE</EcoEyebrow>
                <h1 className="eco-display mt-3 text-[clamp(2.2rem,4.4vw,3.6rem)]">
                  Add your organization to the {ecosystem.label} map
                </h1>
                <p className="mt-5 max-w-xl text-[16px] leading-[1.65] text-muted-foreground">
                  The map tracks the funds, programs, research nodes and
                  technology leaders shaping {ecosystem.label}'s innovation
                  economy. Tell us who you are in its own terms and a member of
                  our research team will review the record before it goes on.
                </p>

                <form onSubmit={handleSubmit} className="mt-10 space-y-8" noValidate>
                  <Section title="Organization">
                    <Field
                      id="eco-name"
                      label="Organization name"
                      required
                      error={fieldErrors.name}
                    >
                      <Input
                        id="eco-name"
                        value={form.name}
                        onChange={(e) => set("name")(e.target.value)}
                        placeholder="1Password"
                        autoComplete="organization"
                      />
                    </Field>

                    <Field
                      id="eco-website"
                      label="Website"
                      required
                      error={fieldErrors.website}
                      hint="A bare domain is fine — we'll add the https://."
                    >
                      <Input
                        id="eco-website"
                        value={form.website}
                        onChange={(e) => set("website")(e.target.value)}
                        placeholder="1password.com"
                        inputMode="url"
                      />
                    </Field>

                    <Wide>
                      <Field
                        id="eco-description"
                        label="Description"
                        required
                        error={fieldErrors.description}
                        hint={`One or two sentences, the way the map reads them: what you are, where you're based, what you focus on. ${form.description.length}/${DESCRIPTION_LIMIT}`}
                      >
                        <Textarea
                          id="eco-description"
                          value={form.description}
                          maxLength={DESCRIPTION_LIMIT}
                          onChange={(e) => set("description")(e.target.value)}
                          placeholder="Technology Company based in Toronto, Ontario. Focus: Cybersecurity / identity. Funding model: VC-backed private."
                          className="min-h-28 resize-y"
                        />
                      </Field>
                    </Wide>
                  </Section>

                  <Section title="Classification">
                    {/* The map calls this a "player type" internally, and the
                        wire field keeps that name — but nobody filling in the
                        form thinks of themselves as a player. */}
                    <Field
                      id="eco-player-type"
                      label="Company type"
                      required
                      error={fieldErrors.player_type}
                    >
                      <Select
                        value={form.player_type}
                        onValueChange={set("player_type")}
                      >
                        <SelectTrigger id="eco-player-type">
                          <SelectValue placeholder="Choose a company type" />
                        </SelectTrigger>
                        <SelectContent>
                          {ECOSYSTEM_PLAYER_TYPES.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>

                    <Field
                      id="eco-gics"
                      label="Industry (GICS)"
                      error={fieldErrors.gics_sub_industry}
                      hint={
                        gicsCodes.length >= MAX_GICS_SUB_INDUSTRIES
                          ? `${MAX_GICS_SUB_INDUSTRIES} of ${MAX_GICS_SUB_INDUSTRIES} chosen — remove one to swap it.`
                          : `Search 1,400+ GICS sub-industries. Up to ${MAX_GICS_SUB_INDUSTRIES}.`
                      }
                    >
                      {/* The same classification the rest of the product tags
                          companies with, so a promoted record lines up with the
                          deals already in the system instead of needing a
                          mapping table. */}
                      <VirtualizedCombobox
                        id="eco-gics"
                        options={gicsOptions}
                        selected={gicsCodes}
                        onToggle={toggleGics}
                        placeholder="Select industries"
                        searchPlaceholder="Search industries…"
                        multiple
                      />
                    </Field>

                    <Field
                      id="eco-sector-focus"
                      label="Sector / focus"
                      error={fieldErrors.sector_focus}
                      hint="The line the profile shows, e.g. “Cybersecurity / identity”."
                    >
                      <Input
                        id="eco-sector-focus"
                        value={form.sector_focus}
                        onChange={(e) => set("sector_focus")(e.target.value)}
                        placeholder="Cybersecurity / identity"
                      />
                    </Field>

                    <OptionalSelect
                      id="eco-funding-model"
                      label="Funding model"
                      placeholder="Choose a funding model"
                      options={ECOSYSTEM_FUNDING_MODELS}
                      value={form.funding_model}
                      onChange={set("funding_model")}
                      error={fieldErrors.funding_model}
                    />

                    <Field
                      id="eco-funding-detail"
                      label="Funding detail"
                      error={fieldErrors.funding_detail}
                      hint="Free text, e.g. “VC-backed private”."
                    >
                      <Input
                        id="eco-funding-detail"
                        value={form.funding_detail}
                        onChange={(e) => set("funding_detail")(e.target.value)}
                        placeholder="VC-backed private"
                      />
                    </Field>

                    <OptionalSelect
                      id="eco-ownership"
                      label="Ownership"
                      placeholder="Choose an ownership type"
                      options={ECOSYSTEM_OWNERSHIP_TYPES}
                      value={form.ownership}
                      onChange={set("ownership")}
                      error={fieldErrors.ownership}
                    />

                    <Wide>
                      <Field
                        id="eco-tags"
                        label="Tags"
                        error={fieldErrors.tags}
                        hint={`Press Enter or comma to add. Up to ${MAX_TAGS}.`}
                      >
                        <Input
                          id="eco-tags"
                          value={tagDraft}
                          disabled={tags.length >= MAX_TAGS}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value.endsWith(",")) addTag(value);
                            else setTagDraft(value);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              addTag(tagDraft);
                            } else if (e.key === "Backspace" && !tagDraft) {
                              setTags((prev) => prev.slice(0, -1));
                            }
                          }}
                          onBlur={() => addTag(tagDraft)}
                          placeholder="Cybersecurity, identity, late-stage"
                        />
                        {tags.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 pt-1">
                            {tags.map((tag) => (
                              <button
                                key={tag}
                                type="button"
                                onClick={() =>
                                  setTags((prev) => prev.filter((t) => t !== tag))
                                }
                                className="inline-flex items-center gap-1.5 border border-border px-2 py-[3px] text-[10px] text-muted-foreground transition-colors hover:border-foreground hover:text-foreground"
                              >
                                {tag}
                                <X className="h-3 w-3" aria-hidden="true" />
                                <span className="sr-only">Remove {tag}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </Field>
                    </Wide>
                  </Section>

                  <Section title="Headquarters">
                    <Field id="eco-city" label="City" required error={fieldErrors.city}>
                      <Input
                        id="eco-city"
                        value={form.city}
                        onChange={(e) => set("city")(e.target.value)}
                        placeholder="Toronto"
                        autoComplete="address-level2"
                      />
                    </Field>

                    <Field
                      id="eco-region"
                      label="Province / territory"
                      required
                      error={fieldErrors.region}
                    >
                      <Select value={form.region} onValueChange={set("region")}>
                        <SelectTrigger id="eco-region">
                          <SelectValue placeholder="Choose a province" />
                        </SelectTrigger>
                        <SelectContent>
                          {ECOSYSTEM_REGIONS.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>

                    <Field
                      id="eco-street"
                      label="Street address"
                      error={fieldErrors.street}
                      hint="Optional. Used to place your pin precisely."
                    >
                      <Input
                        id="eco-street"
                        value={form.street}
                        onChange={(e) => set("street")(e.target.value)}
                        placeholder="4711 Yonge St"
                        autoComplete="address-line1"
                      />
                    </Field>

                    <Field id="eco-line2" label="Suite / floor">
                      <Input
                        id="eco-line2"
                        value={form.line2}
                        onChange={(e) => set("line2")(e.target.value)}
                        placeholder="10th Floor"
                        autoComplete="address-line2"
                      />
                    </Field>

                    <Field id="eco-postal" label="Postal code">
                      <Input
                        id="eco-postal"
                        value={form.postal_code}
                        onChange={(e) => set("postal_code")(e.target.value)}
                        placeholder="M2N 6K8"
                        autoComplete="postal-code"
                      />
                    </Field>

                    <Field id="eco-country" label="Country" error={fieldErrors.country}>
                      <Input
                        id="eco-country"
                        value={form.country}
                        onChange={(e) => set("country")(e.target.value)}
                        autoComplete="country-name"
                      />
                    </Field>
                  </Section>

                  <Section title="Links">
                    <Field
                      id="eco-linkedin"
                      label="LinkedIn"
                      error={fieldErrors.linkedin}
                    >
                      <Input
                        id="eco-linkedin"
                        value={form.linkedin}
                        onChange={(e) => set("linkedin")(e.target.value)}
                        placeholder="linkedin.com/company/1password"
                        inputMode="url"
                      />
                    </Field>

                    <Field
                      id="eco-source"
                      label="Source"
                      error={fieldErrors.source_url}
                      hint="A page backing what you've told us — a press release, an about page, a filing."
                    >
                      <Input
                        id="eco-source"
                        value={form.source_url}
                        onChange={(e) => set("source_url")(e.target.value)}
                        placeholder="1password.com/about"
                        inputMode="url"
                      />
                    </Field>
                  </Section>

                  <Section title="About you">
                    <Field
                      id="eco-submitter-name"
                      label="Your name"
                      required
                      error={fieldErrors.submitter_name}
                    >
                      <Input
                        id="eco-submitter-name"
                        value={form.submitter_name}
                        onChange={(e) => set("submitter_name")(e.target.value)}
                        autoComplete="name"
                      />
                    </Field>

                    <Field
                      id="eco-submitter-email"
                      label="Your email"
                      required
                      error={fieldErrors.submitter_email}
                      hint="Only used to come back to you about this record."
                    >
                      <Input
                        id="eco-submitter-email"
                        type="email"
                        value={form.submitter_email}
                        onChange={(e) => set("submitter_email")(e.target.value)}
                        autoComplete="email"
                      />
                    </Field>

                    <Wide>
                      <Field
                        id="eco-submitter-role"
                        label="Your role"
                        error={fieldErrors.submitter_role}
                      >
                        <Input
                          id="eco-submitter-role"
                          value={form.submitter_role}
                          onChange={(e) => set("submitter_role")(e.target.value)}
                          placeholder="Head of Communications"
                          autoComplete="organization-title"
                        />
                      </Field>
                    </Wide>
                  </Section>

                  {formError && (
                    <div
                      role="alert"
                      className="border border-destructive/40 bg-destructive/5 px-4 py-3 text-[13px] text-destructive"
                    >
                      {formError}
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-4 border-t border-border pt-6">
                    <EcoButton type="submit" disabled={submitting}>
                      {submitting && (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                      )}
                      {submitting ? "Submitting" : "Submit for review"}
                    </EcoButton>
                    <p className="text-[11px] text-muted-foreground">
                      Fields marked <span className="text-destructive">*</span> are
                      required.
                    </p>
                  </div>
                </form>
              </div>

              <aside className="flex flex-col gap-4 lg:sticky lg:top-[92px] lg:self-start">
                <EcoAsideCard title="What happens next">
                  <ol className="space-y-2 text-muted-foreground">
                    <li>
                      <span className="text-foreground">1.</span> Your record lands
                      in our research queue as <EcoBadge>pending</EcoBadge>
                    </li>
                    <li>
                      <span className="text-foreground">2.</span> A researcher
                      verifies it against public sources
                    </li>
                    <li>
                      <span className="text-foreground">3.</span> Approved records
                      appear on the {ecosystem.label} map and get their own profile
                      page
                    </li>
                  </ol>
                </EcoAsideCard>

                <EcoAsideCard title="What we publish">
                  <p className="text-muted-foreground">
                    Everything above the “About you” section is intended for the
                    public profile. Your name, email and role stay internal — they
                    are how we reach you about the record, nothing else.
                  </p>
                </EcoAsideCard>

                <EcoAsideCard title="Already on the map?">
                  <p className="text-muted-foreground">
                    Find your profile and email us the correction instead — filing
                    it twice creates a duplicate we have to merge by hand.
                  </p>
                  <a
                    href={mapUrl}
                    className="mt-3 inline-flex items-center gap-1.5 text-foreground underline underline-offset-4"
                  >
                    Browse the {ecosystem.label} map
                    <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </a>
                </EcoAsideCard>
              </aside>
            </div>
          )}
        </EcoContainer>
      </main>

      <EcoFooter />
    </EcoShell>
  );
}

/** A Select whose value can be cleared back to "not stated". */
function OptionalSelect({
  id,
  label,
  placeholder,
  options,
  value,
  onChange,
  error,
}: {
  id: string;
  label: string;
  placeholder: string;
  options: readonly string[];
  value: string;
  onChange: (value: string) => void;
  error?: string;
}) {
  return (
    <Field id={id} label={label} error={error}>
      <Select
        value={value || NONE}
        onValueChange={(next) => onChange(next === NONE ? "" : next)}
      >
        <SelectTrigger id={id}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>Not stated</SelectItem>
          {options.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}

/** The confirmation — the filed record shown back as the profile it will become. */
function SubmittedPanel({
  name,
  slug,
  ecosystemLabel,
  mapUrl,
  onAnother,
}: {
  name: string;
  slug: string;
  ecosystemLabel: string;
  mapUrl: string;
  onAnother: () => void;
}) {
  return (
    <div className="mt-7 grid gap-10 lg:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)] lg:gap-14">
      <div>
        <EcoEyebrow>SUBMITTED FOR REVIEW</EcoEyebrow>
        <h1 className="eco-display mt-3 text-[clamp(2.2rem,4.4vw,3.6rem)]">
          Thank you — {name} is in the queue
        </h1>
        <p className="mt-5 max-w-xl text-[16px] leading-[1.65] text-muted-foreground">
          A researcher will verify the record against public sources before it
          appears on the {ecosystemLabel} map. We'll email you at the address you
          gave us if we need anything, and again when it's live.
        </p>

        <dl className="mt-10 border-t border-border">
          <FactRow term="Status">
            <EcoBadge>Pending review</EcoBadge>
          </FactRow>
          <FactRow term="Organization">{name}</FactRow>
          <FactRow term="Profile URL">
            <span className="text-muted-foreground">
              /ecosystem/{ecosystemLabel.toLowerCase()}/
            </span>
            {slug}
          </FactRow>
        </dl>

        <div className="mt-8 flex flex-wrap gap-3">
          <EcoButton as="a" href={mapUrl}>
            Back to the {ecosystemLabel} map
          </EcoButton>
          <EcoButton variant="secondary" onClick={onAnother}>
            Submit another organization
          </EcoButton>
        </div>
      </div>

      <aside className="flex flex-col gap-4">
        <EcoAsideCard title="Keep in mind">
          <p className="text-muted-foreground">
            The profile URL above is reserved but not live yet. Nothing you
            submitted is published until a researcher approves it.
          </p>
        </EcoAsideCard>
      </aside>
    </div>
  );
}

function FactRow({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div
      className={cn(
        "grid gap-2 border-b border-border py-3.5 text-[13px] leading-[1.5]",
        "sm:grid-cols-[180px_1fr] sm:gap-5"
      )}
    >
      <dt className="text-[11px] uppercase tracking-[0.06em] text-muted-foreground">
        {term}
      </dt>
      <dd className="m-0">{children}</dd>
    </div>
  );
}
